import { useState, useRef, useEffect, useCallback } from 'react';
import { chatApi, type ChatMessage } from '@/api/client';
import { type Persona } from './PersonaCard';

interface ChatWidgetProps {
  clientId?: string;
  persona?: Persona | null;
  onClose?: () => void;
  language?: string;
}

export function ChatWidget({ clientId, persona, onClose, language = 'en' }: ChatWidgetProps) {
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [input, setInput] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  const t = {
    placeholder: language === 'es' ? 'Escribe tu mensaje...' : 'Type your message...',
    send: language === 'es' ? 'Enviar' : 'Send',
    welcome: language === 'es'
      ? '¡Hola! Soy tu asistente de inteligencia de mercado. ¿En qué puedo ayudarte?'
      : "Hello! I'm your market intelligence assistant. How can I help you?",
    welcomePersona: language === 'es'
      ? `¡Hola! Soy ${persona?.name}, ${persona?.profession}. Pregúntame sobre mis hábitos de compra.`
      : `Hi! I'm ${persona?.name}, ${persona?.profession}. Ask me about my shopping habits.`,
    thinking: language === 'es' ? 'Pensando...' : 'Thinking...',
    error: language === 'es' ? 'Error al enviar mensaje' : 'Failed to send message',
    retry: language === 'es' ? 'Reintentar' : 'Retry',
    suggestions: language === 'es' ? 'Sugerencias' : 'Suggestions',
  };

  // Auto-scroll to bottom on new messages
  const scrollToBottom = useCallback(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, []);

  useEffect(() => {
    scrollToBottom();
  }, [messages, scrollToBottom]);

  // Focus input on mount
  useEffect(() => {
    inputRef.current?.focus();
  }, []);

  // Add welcome message on mount
  useEffect(() => {
    const welcomeMessage: ChatMessage = {
      role: 'assistant',
      content: persona ? t.welcomePersona : t.welcome,
      timestamp: new Date().toISOString(),
    };
    setMessages([welcomeMessage]);
  }, [persona, t.welcome, t.welcomePersona]);

  const sendMessage = async (messageText?: string) => {
    const text = messageText || input.trim();
    if (!text || isLoading) return;

    setError(null);
    const userMessage: ChatMessage = {
      role: 'user',
      content: text,
      timestamp: new Date().toISOString(),
    };

    setMessages(prev => [...prev, userMessage]);
    setInput('');
    setIsLoading(true);

    try {
      // Build context for persona if present
      let contextMessage = text;
      if (persona) {
        contextMessage = `[Context: You are simulating ${persona.name}, a ${persona.age}-year-old ${persona.profession} from ${persona.location}. Segment: ${persona.segment}. Quote: "${persona.quote}". Topics of interest: ${persona.topics?.join(', ')}. Channels: ${persona.channels?.join(', ')}. Respond as this persona would, in first person.]\n\nUser question: ${text}`;
      }

      const response = await chatApi.send(
        contextMessage,
        clientId,
        messages.filter(m => m.role !== 'assistant' || !m.content.includes('Hello!'))
      );

      const assistantMessage: ChatMessage = {
        role: 'assistant',
        content: response.response,
        timestamp: new Date().toISOString(),
      };

      setMessages(prev => [...prev, assistantMessage]);
    } catch (err) {
      setError(t.error);
      console.error('Chat error:', err);
    } finally {
      setIsLoading(false);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  };

  // Suggested prompts based on context
  const suggestedPrompts = persona
    ? [
        language === 'es' ? '¿Qué marcas prefieres?' : 'What brands do you prefer?',
        language === 'es' ? '¿Cómo eliges productos?' : 'How do you choose products?',
        language === 'es' ? '¿Qué tan importante es el precio?' : 'How important is price?',
      ]
    : [
        language === 'es' ? '¿Cuál es mi market share?' : "What's my market share?",
        language === 'es' ? '¿Qué SKUs debo cortar?' : 'Which SKUs should I cut?',
        language === 'es' ? 'Simular aumento de precio' : 'Simulate a price increase',
      ];

  return (
    <div className="flex flex-col h-full bg-surface-800 rounded-xl border border-surface-600 overflow-hidden">
      {/* Header */}
      <div className="flex items-center justify-between px-4 py-3 bg-surface-700 border-b border-surface-600">
        <div className="flex items-center gap-3">
          {persona ? (
            <>
              <span className="text-2xl">{persona.avatar}</span>
              <div>
                <h3 className="font-semibold text-white text-sm">{persona.name}</h3>
                <span className="text-xs text-text-secondary">{persona.segment}</span>
              </div>
            </>
          ) : (
            <>
              <div className="w-8 h-8 rounded-lg bg-primary-500/20 flex items-center justify-center">
                <span className="text-lg">🤖</span>
              </div>
              <div>
                <h3 className="font-semibold text-white text-sm">
                  {language === 'es' ? 'Asistente IA' : 'AI Assistant'}
                </h3>
                <span className="text-xs text-text-secondary">
                  {language === 'es' ? 'Inteligencia de mercado' : 'Market intelligence'}
                </span>
              </div>
            </>
          )}
        </div>
        {onClose && (
          <button
            onClick={onClose}
            className="text-text-secondary hover:text-white p-1 rounded transition-colors"
          >
            <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        )}
      </div>

      {/* Messages */}
      <div className="flex-1 overflow-y-auto p-4 space-y-4">
        {messages.map((message, index) => (
          <MessageBubble key={index} message={message} isPersona={!!persona} language={language} />
        ))}

        {isLoading && (
          <div className="flex items-start gap-3">
            <div className="w-8 h-8 rounded-lg bg-surface-600 flex items-center justify-center flex-shrink-0">
              {persona ? <span className="text-lg">{persona.avatar}</span> : <span className="text-sm">🤖</span>}
            </div>
            <div className="bg-surface-600 rounded-xl rounded-tl-none px-4 py-2.5">
              <div className="flex items-center gap-1">
                <span className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '0ms' }} />
                <span className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '150ms' }} />
                <span className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '300ms' }} />
              </div>
            </div>
          </div>
        )}

        {error && (
          <div className="flex items-center gap-2 p-3 bg-danger-500/10 border border-danger-500/20 rounded-lg">
            <span className="text-danger-400">⚠️</span>
            <span className="text-sm text-danger-400">{error}</span>
            <button
              onClick={() => sendMessage(messages[messages.length - 1]?.content)}
              className="ml-auto text-xs text-primary-400 hover:text-primary-300"
            >
              {t.retry}
            </button>
          </div>
        )}

        <div ref={messagesEndRef} />
      </div>

      {/* Suggestions (show only when few messages) */}
      {messages.length <= 2 && !isLoading && (
        <div className="px-4 pb-2">
          <p className="text-xs text-text-tertiary mb-2">{t.suggestions}</p>
          <div className="flex flex-wrap gap-2">
            {suggestedPrompts.map((prompt, i) => (
              <button
                key={i}
                onClick={() => sendMessage(prompt)}
                className="text-xs px-3 py-1.5 bg-surface-600 hover:bg-surface-500 text-text-secondary rounded-full transition-colors"
              >
                {prompt}
              </button>
            ))}
          </div>
        </div>
      )}

      {/* Input */}
      <div className="p-4 border-t border-surface-600">
        <div className="flex items-center gap-2">
          <input
            ref={inputRef}
            type="text"
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyDown={handleKeyDown}
            placeholder={t.placeholder}
            disabled={isLoading}
            className="flex-1 bg-surface-700 border border-surface-500 rounded-lg px-4 py-2.5 text-sm text-white placeholder:text-text-tertiary focus:border-primary-500 focus:ring-1 focus:ring-primary-500 transition-all disabled:opacity-50"
          />
          <button
            onClick={() => sendMessage()}
            disabled={!input.trim() || isLoading}
            className="px-4 py-2.5 bg-primary-500 hover:bg-primary-400 disabled:bg-surface-600 disabled:cursor-not-allowed text-white text-sm font-medium rounded-lg transition-colors"
          >
            {t.send}
          </button>
        </div>
      </div>
    </div>
  );
}

function MessageBubble({
  message,
  isPersona,
  language,
}: {
  message: ChatMessage;
  isPersona: boolean;
  language: string;
}) {
  const isUser = message.role === 'user';

  if (isUser) {
    return (
      <div className="flex items-start gap-3 justify-end">
        <div className="bg-primary-500/20 text-white rounded-xl rounded-tr-none px-4 py-2.5 max-w-[80%]">
          <p className="text-sm whitespace-pre-wrap">{message.content}</p>
        </div>
        <div className="w-8 h-8 rounded-lg bg-primary-500/30 flex items-center justify-center flex-shrink-0">
          <span className="text-sm">👤</span>
        </div>
      </div>
    );
  }

  return (
    <div className="flex items-start gap-3">
      <div className="w-8 h-8 rounded-lg bg-surface-600 flex items-center justify-center flex-shrink-0">
        {isPersona ? <span className="text-lg">👤</span> : <span className="text-sm">🤖</span>}
      </div>
      <div className="bg-surface-600 rounded-xl rounded-tl-none px-4 py-2.5 max-w-[80%]">
        <p className="text-sm text-gray-200 whitespace-pre-wrap">{message.content}</p>
      </div>
    </div>
  );
}

// Floating chat button for embedding
export function ChatButton({
  onClick,
  language = 'en',
}: {
  onClick: () => void;
  language?: string;
}) {
  return (
    <button
      onClick={onClick}
      className="fixed bottom-6 right-6 w-14 h-14 bg-primary-500 hover:bg-primary-400 text-white rounded-full shadow-lg hover:shadow-xl transition-all flex items-center justify-center group z-50"
      title={language === 'es' ? 'Chat con IA' : 'Chat with AI'}
    >
      <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
        <path
          strokeLinecap="round"
          strokeLinejoin="round"
          strokeWidth={2}
          d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z"
        />
      </svg>
      <span className="absolute -top-10 right-0 bg-surface-800 text-white text-xs px-2 py-1 rounded opacity-0 group-hover:opacity-100 transition-opacity whitespace-nowrap">
        {language === 'es' ? 'Chat con IA' : 'Chat with AI'}
      </span>
    </button>
  );
}
